import { DEV_GARDIAN_PROFILES_OVERRIDE_STORAGE_KEY } from '~~/utils/devOidcProfileOverride'
import { AGENT_AVATAR_COOKIE_KEY, AGENT_AVATAR_STORAGE_KEY } from '~/composables/agent/useMyAgentAvatarSettings'
import { ONBOARDING_STORAGE_KEY } from '~/onboarding/storage'
import { USER_PREFS_COOKIE_KEY, USER_PREFS_STORAGE_KEY } from '~/composables/ui/useUserPreferences'

/** Délai minimum entre deux réinitialisations complètes (évite double-clic). */
const SESSION_RESET_COOLDOWN_MS = 4000

let lastSessionResetAtMs = 0

function getMsSinceLastSessionReset(): number {
  return Date.now() - lastSessionResetAtMs
}

function markSessionResetAttempt(): void {
  lastSessionResetAtMs = Date.now()
}

function isSessionResetCoolingDown(): boolean {
  return getMsSinceLastSessionReset() < SESSION_RESET_COOLDOWN_MS
}

/**
 * Supprime les cookies du domaine courant lisibles via `document.cookie`
 * (chemins `/` et `/auth`, comme sur l’écran d’erreur applicatif).
 */
function clearKnownAppCookies(): void {
  if (!import.meta.client || typeof document === 'undefined') {
    return
  }
  const pathVariants = ['/', '/auth']
  const cookies = document.cookie.split(';')
  for (const raw of cookies) {
    const trimmed = raw.trim()
    const equalIndex = trimmed.indexOf('=')
    const cookieName = equalIndex >= 0 ? trimmed.slice(0, equalIndex) : trimmed
    if (!cookieName) {
      continue
    }
    for (const path of pathVariants) {
      document.cookie = `${cookieName}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=${path}`
    }
  }
}

/** Supprime les cookies de cache des préférences Mon Orga. */
function clearMonOrgaPreferenceCookies(): void {
  if (!import.meta.client || typeof document === 'undefined') {
    return
  }
  const cookieNames = [USER_PREFS_COOKIE_KEY, AGENT_AVATAR_COOKIE_KEY]
  const pathVariants = ['/', '/auth']
  for (const cookieName of cookieNames) {
    for (const path of pathVariants) {
      document.cookie = `${cookieName}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=${path}`
    }
  }
}

/** Supprime les préférences locales Mon Orga et la simulation OIDC dev (sans toucher au reste du domaine). */
function clearOptionalMonOrgaLocalBrowserData(): void {
  if (!import.meta.client) {
    return
  }
  try {
    localStorage.removeItem(USER_PREFS_STORAGE_KEY)
  } catch {
    /* ignore */
  }
  try {
    localStorage.removeItem(ONBOARDING_STORAGE_KEY)
  } catch {
    /* ignore */
  }
  try {
    localStorage.removeItem(AGENT_AVATAR_STORAGE_KEY)
  } catch {
    /* ignore */
  }
  try {
    localStorage.removeItem(DEV_GARDIAN_PROFILES_OVERRIDE_STORAGE_KEY)
  } catch {
    /* ignore */
  }
  clearMonOrgaPreferenceCookies()
}

export interface SessionResetToastHandlers {
  warning(title: string, message: string): void
  info(title: string, message: string): void
  success(title: string, message: string): void
}

export type LogoutFn = () => Promise<void>

/**
 * Déconnexion OIDC si besoin, purge des cookies applicatifs, navigation vers la connexion.
 * @returns `false` si la tentative est ignorée (cooldown).
 */
export async function performAppSessionReset(options: {
  loggedIn: boolean
  logout: LogoutFn
  toast: SessionResetToastHandlers
  loginPath?: string
  /** Ex. `clearError()` avant navigation depuis une boundary Nuxt. */
  beforeNavigate?: () => void
  /** Préférences Mon Orga + simulation OIDC (dev) dans le stockage local du navigateur. */
  clearOptionalLocalData?: boolean
}): Promise<boolean> {
  if (isSessionResetCoolingDown()) {
    options.toast.warning(
      'Veuillez patienter',
      'Une réinitialisation vient d’être tentée. Attendez quelques instants avant de réessayer.',
    )
    return false
  }
  markSessionResetAttempt()

  options.toast.info(
    'Réinitialisation de session',
    'Déconnexion et suppression des cookies locaux en cours…',
  )

  try {
    if (options.loggedIn) {
      await options.logout()
    }
  } catch {
    /* ignore */
  }

  clearKnownAppCookies()

  if (options.clearOptionalLocalData) {
    clearOptionalMonOrgaLocalBrowserData()
  }

  options.toast.success(
    'Session réinitialisée',
    'Vous pouvez vous reconnecter. Si le problème revient, réessayez plus tard.',
  )

  options.beforeNavigate?.()

  const path = options.loginPath ?? '/login'
  void navigateTo({ path })
  return true
}
